import type {
  AddableTitleExtensionFeature,
  TitleExtensionEquipmentRequestBody,
  TitleExtensionEquipmentResponse,
  TitleExtensionEquipmentResponseItem,
  TitleExtensionLanguage,
  TitleExtensionShortName,
} from '@dms/api/sales';

export type AddableEquipmentItem = AddableTitleExtensionFeature;
export type TitleExtensionEquipmentDraftItem = TitleExtensionEquipmentResponseItem;

export type TitleExtensionEquipmentDraftValues = {
  separator: string;
  items: TitleExtensionEquipmentDraftItem[];
};

const hydrateShortNames = (
  shortNames: readonly TitleExtensionShortName[],
  languages: readonly TitleExtensionLanguage[]
): TitleExtensionShortName[] =>
  languages.map((language) => ({
    language,
    shortName: shortNames.find((shortName) => shortName.language === language)?.shortName ?? '',
  }));

export const hydrateTitleExtensionEquipmentDraft = (
  response: TitleExtensionEquipmentResponse
): TitleExtensionEquipmentDraftValues => ({
  separator: response.separator,
  items: response.items.map((item) => ({
    ...item,
    shortNames: hydrateShortNames(item.shortNames, response.languages),
  })),
});

export const serializeTitleExtensionEquipmentDraft = (
  values: TitleExtensionEquipmentDraftValues
): TitleExtensionEquipmentRequestBody => ({
  separator: values.separator,
  items: values.items.map((item) => ({
    featureKey: item.featureKey,
    shortNames: item.shortNames.flatMap((shortName) => {
      const trimmedShortName = shortName.shortName.trim();

      return trimmedShortName.length > 0
        ? [{language: shortName.language, shortName: trimmedShortName}]
        : [];
    }),
  })),
});

export const normalizeTitleExtensionEquipmentDraft = (
  values: TitleExtensionEquipmentDraftValues
): TitleExtensionEquipmentDraftValues => ({
  separator: values.separator,
  items: values.items.map((item) => ({
    ...item,
    shortNames: item.shortNames.map((shortName) => ({
      ...shortName,
      shortName: shortName.shortName.trim(),
    })),
  })),
});

export const buildAvailableTitleExtensionFeatures = (
  addableFeatures: readonly AddableTitleExtensionFeature[],
  removedItems: readonly TitleExtensionEquipmentDraftItem[],
  currentItems: readonly TitleExtensionEquipmentDraftItem[]
): AddableTitleExtensionFeature[] => {
  const featuresByKey = new Map<string, AddableTitleExtensionFeature>();

  addableFeatures.forEach((feature) => featuresByKey.set(feature.featureKey, feature));
  removedItems.forEach((item) =>
    featuresByKey.set(item.featureKey, {featureKey: item.featureKey, name: item.name})
  );

  const currentFeatureKeys = new Set(currentItems.map((item) => item.featureKey));

  return Array.from(featuresByKey.values()).filter(
    (feature) => !currentFeatureKeys.has(feature.featureKey)
  );
};

export const appendTitleExtensionEquipmentFeatures = (
  currentItems: readonly TitleExtensionEquipmentDraftItem[],
  selectedFeatures: readonly AddableTitleExtensionFeature[],
  removedItems: readonly TitleExtensionEquipmentDraftItem[],
  languages: readonly TitleExtensionLanguage[]
): TitleExtensionEquipmentDraftItem[] => {
  const currentFeatureKeys = new Set(currentItems.map((item) => item.featureKey));
  const removedItemsByKey = new Map(removedItems.map((item) => [item.featureKey, item]));

  const appendedItems = selectedFeatures.flatMap((feature) => {
    if (currentFeatureKeys.has(feature.featureKey)) {
      return [];
    }

    currentFeatureKeys.add(feature.featureKey);
    const removedItem = removedItemsByKey.get(feature.featureKey);

    return [
      {
        featureKey: feature.featureKey,
        name: removedItem?.name ?? feature.name,
        shortNames: hydrateShortNames(removedItem?.shortNames ?? [], languages),
      },
    ];
  });

  return [...currentItems, ...appendedItems];
};
